import { useState, useMemo } from 'react';
import { X, Search, TriangleAlert as AlertTriangle, User, UserMinus } from 'lucide-react';
import type { Contact } from '../../../types';
import { wouldCreateCycle, SENIORITY_LABELS } from '../../../lib/orgChartUtils';

interface Props {
  contact: Contact;
  allContacts: Contact[];
  onAssign: (contactId: string, managerId: string | null) => void;
  onClose: () => void;
}

export default function ManagerAssignModal({ contact, allContacts, onAssign, onClose }: Props) {
  const [search, setSearch] = useState('');

  const currentManager = contact.reports_to_id
    ? allContacts.find(c => c.id === contact.reports_to_id)
    : null;

  const candidates = useMemo(() => {
    const q = search.toLowerCase().trim();
    return allContacts
      .filter(c => {
        if (c.id === contact.id) return false;
        if (!q) return true;
        const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
        return (
          fullName.includes(q) ||
          c.role.toLowerCase().includes(q) ||
          c.department.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.org_level - b.org_level);
  }, [allContacts, contact.id, search]);

  const handleSelect = (managerId: string | null) => {
    if (managerId && wouldCreateCycle(allContacts, contact.id, managerId)) {
      return;
    }
    onAssign(contact.id, managerId);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-app-surface rounded-lg shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-app-line">
          <div>
            <h3 className="text-sm font-semibold text-app-ink">Assign Manager</h3>
            <p className="text-xs text-app-muted mt-0.5">
              {contact.first_name} {contact.last_name} - {contact.role}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-app-muted hover:text-app-muted rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-app-line">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, role, or department..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-app-canvas border border-app-line rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-300 focus:border-forest-400 transition-colors"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-app-line">
          <button
            onClick={() => handleSelect(null)}
            className={`w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-app-canvas transition-colors ${
              !contact.reports_to_id ? 'bg-forest-50' : ''
            }`}
          >
            <div className="w-8 h-8 rounded-lg bg-app-canvas flex items-center justify-center flex-shrink-0">
              <UserMinus className="w-4 h-4 text-app-muted" />
            </div>
            <div>
              <p className="text-sm font-medium text-app-ink">No Manager (Top Level)</p>
              <p className="text-[10px] text-app-muted">Make this person a root node in the org chart</p>
            </div>
            {!contact.reports_to_id && (
              <span className="ml-auto text-[10px] font-semibold text-forest-600 bg-forest-100 px-2 py-0.5 rounded-full">Current</span>
            )}
          </button>

          {candidates.map(c => {
            const isCurrent = contact.reports_to_id === c.id;
            const wouldCycle = wouldCreateCycle(allContacts, contact.id, c.id);

            return (
              <button
                key={c.id}
                onClick={() => !wouldCycle && handleSelect(c.id)}
                disabled={wouldCycle}
                className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                  wouldCycle
                    ? 'opacity-40 cursor-not-allowed bg-app-critical/10'
                    : isCurrent
                    ? 'bg-forest-50 hover:bg-forest-100'
                    : 'hover:bg-app-canvas'
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-app-canvas flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-app-muted" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-app-ink truncate">{c.first_name} {c.last_name}</p>
                    {wouldCycle && <AlertTriangle className="w-3 h-3 text-app-critical flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-app-muted truncate">{c.role}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {c.department && (
                      <span className="text-[10px] text-app-muted">{c.department}</span>
                    )}
                    {c.seniority && (
                      <span className="text-[10px] text-app-muted">{SENIORITY_LABELS[c.seniority]?.split('(')[0]?.trim()}</span>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {isCurrent && (
                    <span className="text-[10px] font-semibold text-forest-600 bg-forest-100 px-2 py-0.5 rounded-full">Current</span>
                  )}
                  {wouldCycle && (
                    <span className="text-[10px] font-semibold text-app-critical bg-app-critical/10 px-2 py-0.5 rounded-full">Circular</span>
                  )}
                </div>
              </button>
            );
          })}

          {candidates.length === 0 && search && (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-app-muted">No contacts match "{search}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
